import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { forgotPassword, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/site/Logo";

export default function GlemtAdgangskode() {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await forgotPassword(email.trim());
            setSent(true);
        } catch (err) {
            setError(formatApiError(err) || "Kunne ikke sende reset-link. Prøv igen.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-4 py-12">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-8">
                    <Logo />
                </div>
                <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-200/70 p-7" data-testid="forgot-password-card">
                    <h1 className="text-2xl font-bold text-slate-900 mb-1">Glemt adgangskode?</h1>
                    <p className="text-sm text-slate-500 mb-6">
                        Indtast din email — vi sender dig et link til at vælge en ny adgangskode.
                    </p>

                    {sent ? (
                        <div className="text-center py-6" data-testid="forgot-password-success">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
                                <CheckCircle2 className="w-7 h-7" />
                            </div>
                            <h2 className="text-lg font-semibold text-slate-900 mb-2">Check din indbakke</h2>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Hvis <span className="font-medium">{email}</span> er registreret hos os,
                                får du et reset-link inden for et par minutter.
                            </p>
                            <p className="text-xs text-slate-400 mt-4">
                                Kan du ikke finde mailen? Tjek spam / uønsket post.
                            </p>
                            <Link
                                to="/login"
                                data-testid="forgot-password-back-login"
                                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mt-6"
                            >
                                <ArrowLeft className="w-4 h-4" />Tilbage til login
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div className="space-y-1">
                                <Label htmlFor="email" className="text-xs font-medium text-slate-700">Email</Label>
                                <div className="relative">
                                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        placeholder="navn@firma.dk"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="forgot-password-email-input"
                                        className="h-11 pl-9 bg-slate-50/60 border-slate-200"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div data-testid="forgot-password-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="forgot-password-submit-btn"
                                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Send reset-link
                            </Button>
                        </form>
                    )}

                    {!sent && (
                        <p className="text-sm text-slate-500 text-center mt-6">
                            <Link to="/login" data-testid="forgot-password-login-link" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
                                <ArrowLeft className="w-3 h-3" />Tilbage til login
                            </Link>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
